import { InsightState, InvestigationStatus } from '../utils/constants';

import type { GetFilesPayload } from './files.api';
import type {
  GetInvestigationInsightsResponse,
  Inquiry,
  Insight,
  InsightsByState,
  Investigation,
  RawFile,
} from './files.types';

export const normalizeFile = (file: RawFile): Investigation | Insight | Inquiry => {
  if (file.fileType === 'investigation') {
    return {
      ...file,
      fileDetails: {
        title: file.data.traceName,
        username: file.data.scope?.userName ?? null,
        host: file.data.scope?.systemName ?? null,
        ip: file.data.scope?.ipAddress ?? null,
        startDate: file.data.dateFrom,
        endDate: file.data.dateTo,
      },
      status: file.data.status,
      findings: {
        totalFindings: file.data.outcome?.totalFindingsFound ?? null,
        totalEvents: file.data.outcome?.totalRawProcessed ?? null,
        numberOfInsights: { ...file.data.numberOfInsights },
      },
    };
  }
  if (file.fileType === 'inquiry') {
    return {
      ...file,
      fileDetails: {
        title: file.data.name,
        username: file.data.criteria.user_name,
        startDate: file.data.dateFrom,
        endDate: file.data.dateTo,
      },
      status: file.data.status,
      findings: {
        totalFindings: file.data.inquiryTrace?.totalActivitiesFound ?? null,
        totalEvents: file.data.inquiryTrace?.totalFindingsFound ?? null,
      },
    };
  }

  return {
    ...file,
    fileDetails: {
      title: file.data.name,
      username: file.data.criteria.key_value,
      startDate: file.data.criteria.start_ts,
      endDate: file.data.criteria.end_ts,
    },
    status: InvestigationStatus.COMPLETED, // insights are always completed as they are generated from completed investigations
    findings: {
      totalFindings: file.data.insightTraceResponse?.totalActivitiesFound ?? null,
      totalEvents: file.data.insightTraceResponse?.totalFindingsFound ?? null,
    },
  };
};

export const serializeGetFilesQueryArgs = (queryArgs: GetFilesPayload): string => {
  const { page, size, forCurrentUser, filters, timeAmount, timeUnit, sort } = queryArgs;

  const sortedFilters = Object.fromEntries(Object.entries(filters ?? {}).sort(([a], [b]) => a.localeCompare(b)));

  return `getFiles-${page}-${size}-${forCurrentUser}-${timeAmount}-${timeUnit}-${JSON.stringify(sortedFilters)}-${JSON.stringify(sort ?? null)}`;
};

export const groupInsightsByState = (response: GetInvestigationInsightsResponse): InsightsByState =>
  response.content.reduce<InsightsByState>(
    (acc, insight) => {
      const key = insight.state.toLowerCase() as keyof InsightsByState;
      acc[key].push(insight);
      return acc;
    },
    {
      [InsightState.OPEN.toLowerCase()]: [],
      [InsightState.CLOSED.toLowerCase()]: [],
      [InsightState.UNACCEPTED.toLowerCase()]: [],
      [InsightState.DISMISSED.toLowerCase()]: [],
    } as unknown as InsightsByState,
  );
